import { useEffect, useState, useRef, useCallback, createContext, useContext } from "react";
import "@/App.css";
import "@/index.css";
import { BrowserRouter, Routes, Route, useNavigate, useLocation, Navigate, Link } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { PaywallProvider } from "@/components/PaywallContext";

// Pages
import LandingPage from "@/pages/LandingPage";
import AuthPage from "@/pages/AuthPage";
import AddTransactionPage from "@/pages/AddTransactionPage";
import DashboardPage from "@/pages/DashboardPage";
import CalendarPage from "@/pages/CalendarPage";
import BillsPage from "@/pages/BillsPage";
import GoalsPage from "@/pages/GoalsPage";
import ScenariosPage from "@/pages/ScenariosPage";
import InsightsPage from "@/pages/InsightsPage";
import EducationPage from "@/pages/EducationPage";
import SettingsPage from "@/pages/SettingsPage";
import AdminDashboard from "@/pages/AdminDashboard";
import OnboardingPage from "@/pages/OnboardingPage";
import PaymentSuccessPage from "@/pages/PaymentSuccessPage";
import PaymentCancelledPage from "@/pages/PaymentCancelledPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Configure axios interceptor to automatically add Authorization header
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor to handle auth errors globally
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    // If 401 Unauthorized, clear token and redirect to login
    if (error.response?.status === 401) {
      localStorage.removeItem('auth_token');
      // Don't redirect if already on auth page
      if (!window.location.pathname.includes('/auth')) {
        window.location.href = '/auth';
      }
    }
    return Promise.reject(error);
  }
);

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    
    // Check if we have a stored token
    const token = localStorage.getItem('auth_token');
    if (!token) {
      // No token stored, user is not logged in
      setUser(null);
      setLoading(false);
      return;
    }
    
    try {
      // Token exists, verify it with the server
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
    } catch (error) {
      // Token is invalid or expired, clear it
      localStorage.removeItem('auth_token');
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = (userData, token) => {
    setUser(userData);
    if (token) {
      localStorage.setItem('auth_token', token);
    }
  };

  const logout = async (navigate) => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (e) {
      console.error('Logout error:', e);
    }
    // Fully clear all auth state
    setUser(null);
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user');
    sessionStorage.clear();
    // Clear any cookies that might persist
    document.cookie.split(";").forEach(c => {
      document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
    });
    // Redirect to login page
    if (navigate) {
      navigate('/auth');
    } else {
      window.location.href = '/auth';
    }
  };

  const updateUser = (updates) => {
    setUser(prev => ({ ...prev, ...updates }));
  };

  // Function to refresh user data from server
  const refreshUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
      return response.data;
    } catch (error) {
      console.error('Error refreshing user:', error);
      return null;
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, updateUser, checkAuth, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

// Auth Callback Component
const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = location.hash;
      const sessionId = hash.split('session_id=')[1]?.split('&')[0];

      if (!sessionId) {
        navigate('/auth');
        return;
      }

      try {
        const response = await axios.post(`${API}/auth/session`, 
          { session_id: sessionId },
          { withCredentials: true }
        );
        
        // Store the token from OAuth response
        if (response.data.token) {
          localStorage.setItem('auth_token', response.data.token);
        }
        
        login(response.data.user, response.data.token);
        toast.success(`Welcome, ${response.data.user.name}!`);
        
        // Redirect based on onboarding completion status from database
        if (response.data.user?.onboarding_completed === true) {
          navigate('/calendar', { replace: true });
        } else {
          navigate('/onboarding', { replace: true });
        }
      } catch (error) {
        console.error('Auth callback error:', error);
        toast.error('Authentication failed');
        navigate('/auth');
      }
    };

    processAuth();
  }, [location, navigate, login]);

  return (
    <div className="min-h-screen bg-p2p-dark flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-p2p-gold mx-auto mb-4"></div>
        <p className="text-p2p-muted">Signing you in...</p>
      </div>
    </div>
  );
};

// Protected Route Component - For regular app routes
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-p2p-dark flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-p2p-gold"></div>
      </div>
    );
  }

  // Not authenticated -> redirect to login
  if (!user) {
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  // Routes that don't require onboarding completion check
  const excludeFromOnboarding = ['/settings'];
  
  // If user is on onboarding page but has COMPLETED onboarding -> redirect to dashboard
  if (location.pathname === '/onboarding' && user.onboarding_completed) {
    return <Navigate to="/calendar" replace />;
  }
  
  // If user has NOT completed onboarding and is trying to access other protected routes
  // -> redirect to onboarding (but exclude settings)
  if (!user.onboarding_completed && !excludeFromOnboarding.includes(location.pathname) && location.pathname !== '/onboarding') {
    return <Navigate to="/onboarding" replace />;
  }

  return children;
};

// Admin Route Component - Separate from ProtectedRoute
const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-p2p-dark flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-p2p-gold"></div>
      </div>
    );
  }

  // Not authenticated -> redirect to login
  if (!user) {
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  // Authenticated but not admin -> show access denied
  if (!user.is_admin) {
    return (
      <div className="min-h-screen bg-p2p-dark flex items-center justify-center">
        <div className="text-center p-8">
          <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl">🚫</span>
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">Access Denied</h1>
          <p className="text-p2p-muted mb-6">You don&apos;t have permission to access the admin dashboard.</p>
          <Button onClick={() => window.location.href = '/calendar'} className="btn-gold">
            Go to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  // Admin user -> show admin dashboard (no onboarding redirect for admins)
  return children;
};

// App Router with session_id detection
const AppRouter = () => {
  const location = useLocation();

  // Check URL fragment for session_id synchronously during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/auth" element={<AuthPage />} />
      <Route path="/login" element={<AuthPage />} />
      
      {/* Admin Route - Separate from ProtectedRoute, no onboarding redirect */}
      <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
      
      {/* Protected App Routes - Subject to onboarding redirect */}
      <Route path="/onboarding" element={<ProtectedRoute><OnboardingPage /></ProtectedRoute>} />
      <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      <Route path="/calendar" element={<ProtectedRoute><CalendarPage /></ProtectedRoute>} />
      <Route path="/add" element={<ProtectedRoute><AddTransactionPage /></ProtectedRoute>} />
      <Route path="/bills" element={<ProtectedRoute><BillsPage /></ProtectedRoute>} />
      <Route path="/goals" element={<ProtectedRoute><GoalsPage /></ProtectedRoute>} />
      <Route path="/scenarios" element={<ProtectedRoute><ScenariosPage /></ProtectedRoute>} />
      <Route path="/insights" element={<ProtectedRoute><InsightsPage /></ProtectedRoute>} />
      <Route path="/education" element={<ProtectedRoute><EducationPage /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
      
      {/* Payment Routes */}
      <Route path="/payment-success" element={<ProtectedRoute><PaymentSuccessPage /></ProtectedRoute>} />
      <Route path="/payment-cancelled" element={<ProtectedRoute><PaymentCancelledPage /></ProtectedRoute>} />
      
      {/* Catch-all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <PaywallProvider>
          <AppRouter />
          <Toaster 
            position="top-right" 
            toastOptions={{
              style: {
                background: '#0F5F5C',
                color: '#F5F7F6',
                border: '1px solid rgba(255,255,255,0.1)'
              }
            }}
          />
        </PaywallProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export { API, BACKEND_URL };
export default App;
